import { Dialog, DialogTitle, DialogContent, DialogActions, Box, Typography, IconButton, Button, Stack, Chip, Divider } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import dayjs from "dayjs";
import RichEditor from "../../../components/rich-editor";
import AttendanceEditor from "./AttendanceEditor";
import { dialogCloseSx, primaryButtonSx, countChipSx } from "./buttonStyles";
import { isEmptyHtml } from "../utils/html";

/**
 * A single session opened from the calendar: what it covers, and who was there. Attendance is
 * editable here too, so a day cell is a working view rather than a read-only one.
 */
const TrainingDetailsDialog = ({ open, onClose, training, roster = [], onMarkAttended, onRemoveAttendance }) => {
	if (!training) return null;

	return (
		<Dialog
			open={open}
			onClose={onClose}
			fullWidth
			maxWidth="sm"
			slotProps={{
				paper: {
					sx: {
						borderRadius: 3,
						// Elementor sizes the paper to its content and ignores MUI's maxWidth, so the sm
						// breakpoint is pinned here. Width stays margin-aware so it never overflows.
						m: { xs: 2, sm: 4 },
						width: { xs: "calc(100% - 32px) !important", sm: "calc(100% - 64px) !important" },
						maxWidth: "600px !important",
					},
				},
			}}
		>
			<DialogTitle sx={{ pb: 1 }}>
				<Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
					<Box sx={{ minWidth: 0 }}>
						<Typography variant="h6" sx={{ fontWeight: 700 }}>
							{training.title}
						</Typography>
						<Typography variant="body2" color="text.secondary" noWrap>
							{training.curriculumTitle}
						</Typography>

						<Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
							<Chip
								size="small"
								icon={<EventOutlinedIcon />}
								label={dayjs(training.date).format("ddd, MMM D, YYYY")}
								sx={{ ...countChipSx, cursor: "default", "& .MuiChip-icon": { color: "#1A3F7A !important", fontSize: 15 } }}
							/>
							<Chip
								size="small"
								label={`${training.attendees.length} attended`}
								sx={{ ...countChipSx, cursor: "default", fontSize: "0.7rem" }}
							/>
						</Stack>
					</Box>

					<IconButton onClick={onClose} size="small" aria-label="Close training details" sx={dialogCloseSx}>
						<CloseIcon />
					</IconButton>
				</Stack>
			</DialogTitle>

			<DialogContent dividers sx={{ width: "100%" }}>
				<Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: 0.6, color: "text.secondary", textTransform: "uppercase" }}>
					Description
				</Typography>

				<Box sx={{ mt: 0.5, mb: 2 }}>
					{isEmptyHtml(training.description) ? (
						<Typography variant="body2" color="text.secondary">
							No description provided.
						</Typography>
					) : (
						<RichEditor value={training.description} readOnly />
					)}
				</Box>

				<Divider sx={{ mb: 2 }} />

				<Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: 0.6, color: "text.secondary", textTransform: "uppercase" }}>
					Attendance
				</Typography>

				<Box sx={{ mt: 1 }}>
					<AttendanceEditor
						training={training}
						roster={roster}
						onMarkAttended={(agent) => onMarkAttended(training.curriculumId, training.id, agent)}
						onRemoveAttendance={(agentId) => onRemoveAttendance(training.curriculumId, training.id, agentId)}
						maxListHeight={300}
					/>
				</Box>
			</DialogContent>

			<DialogActions sx={{ px: 3, py: 2 }}>
				<Button onClick={onClose} variant="contained" sx={primaryButtonSx}>
					Done
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default TrainingDetailsDialog;
